import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { FAQ } from "@/routes/Unauth/FAQ";
import { SUPPORTED_LOCALES, Locale } from "@/constants/landingPages";
import { generateFAQSchema } from "@/utils/seo";

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQContent {
  meta: {
    title: string;
    description: string;
  };
  hero: {
    title: string;
    subtitle: string;
  };
  faqs: FAQItem[];
  cta: {
    title: string;
    description: string;
    primaryButton: {
      text: string;
      url: string;
    };
    secondaryButton: {
      text: string;
      url: string;
    };
  };
}

interface FAQPageProps {
  content: FAQContent;
  locale: Locale;
}

export default function FAQPage({ content, locale }: FAQPageProps) {
  const baseUrl = process.env.NEXT_PUBLIC_URL || "https://flexibooking.sk";
  const faqUrl = `${baseUrl}/${locale}/faq`;

  // Generate FAQ Schema for SEO
  const faqSchema = generateFAQSchema({
    faqs: content.faqs,
    url: faqUrl,
  });

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema).replace(/</g, "\\u003c"),
          }}
        />
      </Head>
      <FAQ content={content} locale={locale} />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = SUPPORTED_LOCALES.map((locale) => ({
    params: { locale },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<FAQPageProps> = async ({
  params,
}) => {
  const locale = params?.locale as Locale;

  try {
    // Try to load content for the specific locale
    const content = await import(`@/content/faq/${locale}.json`);

    return {
      props: {
        content: content.default as FAQContent,
        locale,
      },
    };
  } catch (error) {
    // Fallback to Slovak if locale content doesn't exist
    const content = await import(`@/content/faq/sk.json`);

    return {
      props: {
        content: content.default as FAQContent,
        locale,
      },
    };
  }
};
